import React from 'react';
import { Link } from 'react-router-dom';
import { Container, Row, Col } from 'reactstrap';
import Navbar from './Navbar';
import Footer from './Footer';
import GuestChatWidget from './GuestChatWidget';
import './landing.scss';
import SeoHead from '../../../Components/Common/SeoHead';
import HelpBreadcrumbSchema from '../../../Components/Common/HelpBreadcrumbSchema';
import HelpScreenshot from './HelpScreenshot';
import type { HelpImage } from './HelpScreenshot';

const HelpArticlePricing: React.FC = () => {
  const BASE = '/images/help/pricing';

  const screenshots: Record<string, HelpImage[]> = {
    step1: [
      { src: `${BASE}/prc-step-1.png`, alt: 'Smart repricing overview', caption: 'Step 1 – How Coderom keeps your eBay prices in sync with Amazon' },
    ],
    step2: [
      { src: `${BASE}/prc-step-2.png`, alt: 'Profit margin settings', caption: 'Step 2 – Set your target profit margin' },
    ],
    step3: [
      { src: `${BASE}/prc-step-3.png`, alt: 'eBay fees configuration', caption: 'Step 3 – Configure eBay and payment fees' },
    ],
    step4: [
      { src: `${BASE}/prc-step-4.png`, alt: 'Pricing formula breakdown', caption: 'Step 4 – The pricing formula behind every update' },
    ],
    step5: [
      { src: `${BASE}/prc-step-5.png`, alt: 'Min and max price boundaries', caption: 'Step 5 – Set minimum and maximum price boundaries' },
    ],
    step6: [
      { src: `${BASE}/prc-step-6.png`, alt: 'Repricing schedule settings', caption: 'Step 6 – Smart repricing check schedule' },
    ],
  };

  return (
    <div className="layout-wrapper landing">
      <SeoHead
        title="Smart Repricing & Pricing Rules | CodeRom Help"
        description="Set up CodeRom's smart repricing to stay competitive and protect your margins."
      />
      <HelpBreadcrumbSchema slug="pricing" name="Smart repricing & pricing rules" />
      <Navbar />

      <div className="help-article-page">
        <Container>
          <Row className="justify-content-center">
            <Col lg={9} xl={8}>
              {/* Breadcrumb */}
              <div className="breadcrumb-section">
                <Link to="/help" className="breadcrumb-link">Help Center</Link>
                <span className="breadcrumb-separator">›</span>
                <span className="breadcrumb-current">Smart repricing & pricing rules</span>
              </div>

              {/* Article Header */}
              <div className="article-header">
                <h1>Smart repricing & pricing rules</h1>
                <p className="article-subtitle">
                  Keep your eBay prices automatically in sync with Amazon. Set your profit margins, fees, and pricing rules — Coderom handles the rest.
                </p>
                <div className="article-meta">
                  <span className="read-time">📖 8 min read</span>
                  <span className="category">Pricing</span>
                </div>
              </div>

              {/* Table of Contents */}
              <div className="table-of-contents">
                <h3>📋 On this page</h3>
                <ul>
                  <li><a href="#step1">1. How smart repricing works</a></li>
                  <li><a href="#step2">2. Set your profit margin</a></li>
                  <li><a href="#step3">3. Configure eBay fees</a></li>
                  <li><a href="#step4">4. Pricing formula explained</a></li>
                  <li><a href="#step5">5. Manage min/max prices</a></li>
                  <li><a href="#step6">6. Repricing schedule</a></li>
                </ul>
              </div>

              {/* Article Content */}
              <div className="article-content">

                {/* Step 1 */}
                <div id="step1" className="content-section">
                  <h2>Step 1: How smart repricing works</h2>
                  <p>
                    When the price of a product changes on Amazon, Coderom automatically updates your eBay listing price. This keeps your margins consistent and ensures you never sell at a loss.
                  </p>
                  <p>
                    The repricing cycle works like this:
                  </p>
                  <ol>
                    <li>Coderom monitors Amazon prices regularly (every few hours)</li>
                    <li>When a price change is detected, the new eBay price is calculated using your pricing rules</li>
                    <li>Your eBay listing is updated automatically via the eBay API</li>
                  </ol>

                  <HelpScreenshot images={screenshots.step1} stepLabel="Step 1: How smart repricing works" />

                  <div className="tip-box">
                    <div className="tip-icon">💡</div>
                    <div className="tip-content">
                      <strong>Tip:</strong> You can see recent price changes in your <strong>Active Products</strong> list — look for the price change indicator next to each product.
                    </div>
                  </div>
                </div>

                {/* Step 2 */}
                <div id="step2" className="content-section">
                  <h2>Step 2: Set your profit margin</h2>
                  <ol>
                    <li>Go to <strong>Settings → Pricing</strong></li>
                    <li>Find the <strong>Profit Margin</strong> setting</li>
                    <li>Enter your desired margin percentage (e.g. 25%)</li>
                    <li>Click <strong>Save</strong></li>
                  </ol>

                  <p>
                    This margin is added on top of the Amazon price plus all fees. For example:
                  </p>
                  <ul>
                    <li>Amazon price: £20.00</li>
                    <li>eBay fees (12.8%): £3.28</li>
                    <li>Your margin (25%): £5.82</li>
                    <li><strong>Final eBay price: £29.10</strong></li>
                  </ul>

                  <HelpScreenshot images={screenshots.step2} stepLabel="Step 2: Set your profit margin" />
                </div>

                {/* Step 3 */}
                <div id="step3" className="content-section">
                  <h2>Step 3: Configure eBay fees</h2>
                  <p>
                    eBay charges various fees that affect your profit. Go to <strong>Settings → Pricing</strong> and set:
                  </p>

                  <div className="fees-table">
                    <table>
                      <thead>
                        <tr>
                          <th>Fee</th>
                          <th>Description</th>
                          <th>Typical value</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td><strong>eBay final value fee</strong></td>
                          <td>Percentage charged on each sale</td>
                          <td>12.8% (UK)</td>
                        </tr>
                        <tr>
                          <td><strong>PayPal/payment fee</strong></td>
                          <td>Payment processing fee</td>
                          <td>2.9% + £0.30</td>
                        </tr>
                        <tr>
                          <td><strong>Promoted listing fee</strong></td>
                          <td>If you use eBay advertising</td>
                          <td>2-5% (optional)</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <HelpScreenshot images={screenshots.step3} stepLabel="Step 3: Configure eBay fees" />

                  <div className="tip-box">
                    <div className="tip-icon">💡</div>
                    <div className="tip-content">
                      <strong>Tip:</strong> Coderom includes these fees in the price calculation automatically. Make sure your fee percentages match your actual eBay fee structure — check your eBay seller dashboard for exact rates.
                    </div>
                  </div>
                </div>

                {/* Step 4 */}
                <div id="step4" className="content-section">
                  <h2>Step 4: Pricing formula explained</h2>
                  <p>
                    Coderom calculates your eBay price using this formula:
                  </p>
                  <div style={{
                    background: '#f8fafc',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    padding: '20px',
                    margin: '20px 0',
                    fontFamily: 'monospace',
                    fontSize: '15px',
                    textAlign: 'center'
                  }}>
                    <strong>eBay Price = (Amazon Price + Shipping) ÷ (1 − eBay Fee% − Payment Fee%) × (1 + Profit Margin%)</strong>
                  </div>
                  <p>
                    You don't need to calculate this yourself — just set your margin and fees, and Coderom does the maths.
                  </p>

                  <HelpScreenshot images={screenshots.step4} stepLabel="Step 4: Pricing formula explained" />
                </div>

                {/* Step 5 */}
                <div id="step5" className="content-section">
                  <h2>Step 5: Manage min/max prices</h2>
                  <p>
                    To protect against pricing errors, you can set price boundaries:
                  </p>
                  <ol>
                    <li>Go to <strong>Settings → Pricing</strong></li>
                    <li>Set <strong>Minimum Price</strong> — your listing will never go below this price</li>
                    <li>Set <strong>Maximum Price</strong> — your listing will never go above this price</li>
                    <li>These can be set globally or per product</li>
                  </ol>

                  <HelpScreenshot images={screenshots.step5} stepLabel="Step 5: Manage min/max prices" />

                  <div className="tip-box">
                    <div className="tip-icon">💡</div>
                    <div className="tip-content">
                      <strong>Tip:</strong> Setting a minimum price is especially important. If an Amazon price drops dramatically (e.g. during a flash sale), the minimum price prevents your eBay listing from being underpriced.
                    </div>
                  </div>
                </div>

                {/* Step 6 */}
                <div id="step6" className="content-section">
                  <h2>Step 6: Repricing schedule</h2>
                  <p>
                    Coderom checks Amazon prices on a smart schedule:
                  </p>

                  <div className="schedule-table">
                    <table>
                      <thead>
                        <tr>
                          <th>Product status</th>
                          <th>Check frequency</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>Recently sold</td>
                          <td>Every 6 hours</td>
                        </tr>
                        <tr>
                          <td>Active with views</td>
                          <td>Every 12 hours</td>
                        </tr>
                        <tr>
                          <td>Active, low activity</td>
                          <td>Every 24 hours</td>
                        </tr>
                        <tr>
                          <td>Out of stock on Amazon</td>
                          <td>Every 48 hours</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <p>
                    You don't need to configure this — it runs automatically. Products that sell more often are checked more frequently.
                  </p>

                  <HelpScreenshot images={screenshots.step6} stepLabel="Step 6: Repricing schedule" />
                </div>

                {/* What's Next */}
                <div className="whats-next-section">
                  <h2>What's next?</h2>
                  <p>Now that you understand smart repricing, explore these features:</p>
                  <div className="next-steps">
                    <Link to="/help/safe-repricing" className="next-step-card">
                      <div className="next-step-icon">🛡️</div>
                      <div className="next-step-content">
                        <h4>Safe repricing protection</h4>
                        <p>Protect your listings from sudden Amazon price spikes or drops</p>
                      </div>
                    </Link>
                    <Link to="/help/stock" className="next-step-card">
                      <div className="next-step-icon">📦</div>
                      <div className="next-step-content">
                        <h4>Stock synchronisation & inventory</h4>
                        <p>Keep your eBay listings in sync with Amazon stock levels 24/7</p>
                      </div>
                    </Link>
                    <Link to="/help/orders" className="next-step-card">
                      <div className="next-step-icon">📋</div>
                      <div className="next-step-content">
                        <h4>Order management & fulfilment</h4>
                        <p>Automatically process eBay orders and track shipments</p>
                      </div>
                    </Link>
                  </div>
                </div>

                {/* Was This Helpful */}
                <div className="helpful-section">
                  <h3>Was this helpful?</h3>
                  <div className="helpful-buttons">
                    <button className="helpful-btn helpful-yes">👍 Yes</button>
                    <button className="helpful-btn helpful-no">👎 No</button>
                  </div>
                  <p className="helpful-text">
                    Still need help? <Link to="/help">Browse all articles</Link> or{' '}
                    <a href="mailto:support@coderom.com">contact support</a>.
                  </p>
                </div>

              </div>
            </Col>
          </Row>
        </Container>
      </div>

      <style>{`
        .help-article-page {
          padding: 120px 0 80px;
          background: linear-gradient(180deg, #f8fafc 0%, #ffffff 100%);
          min-height: 100vh;
        }

        /* Breadcrumb */
        .breadcrumb-section {
          margin-bottom: 24px;
          font-size: 14px;
        }
        .breadcrumb-link {
          color: #6366f1;
          text-decoration: none;
          transition: color 0.2s;
        }
        .breadcrumb-link:hover {
          color: #4f46e5;
        }
        .breadcrumb-separator {
          margin: 0 8px;
          color: #cbd5e1;
        }
        .breadcrumb-current {
          color: #64748b;
        }

        /* Article Header */
        .article-header {
          margin-bottom: 40px;
        }
        .article-header h1 {
          font-size: 42px;
          font-weight: 700;
          color: #1e293b;
          margin-bottom: 16px;
          line-height: 1.2;
        }
        .article-subtitle {
          font-size: 18px;
          color: #64748b;
          line-height: 1.6;
          margin-bottom: 20px;
        }
        .article-meta {
          display: flex;
          gap: 16px;
          align-items: center;
          font-size: 14px;
        }
        .read-time {
          color: #64748b;
        }
        .category {
          background: #f1f5f9;
          color: #6366f1;
          padding: 4px 12px;
          border-radius: 12px;
          font-weight: 500;
        }

        /* Table of Contents */
        .table-of-contents {
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          padding: 24px;
          margin-bottom: 40px;
        }
        .table-of-contents h3 {
          font-size: 16px;
          font-weight: 600;
          color: #1e293b;
          margin-bottom: 16px;
        }
        .table-of-contents ul {
          list-style: none;
          padding: 0;
          margin: 0;
        }
        .table-of-contents li {
          margin-bottom: 8px;
        }
        .table-of-contents a {
          color: #6366f1;
          text-decoration: none;
          font-size: 15px;
          transition: color 0.2s;
        }
        .table-of-contents a:hover {
          color: #4f46e5;
          text-decoration: underline;
        }

        /* Article Content */
        .article-content {
          color: #334155;
          font-size: 16px;
          line-height: 1.8;
        }
        .content-section {
          margin-bottom: 48px;
          scroll-margin-top: 100px;
        }
        .content-section h2 {
          font-size: 28px;
          font-weight: 700;
          color: #1e293b;
          margin-bottom: 16px;
          margin-top: 40px;
        }
        .content-section h3 {
          font-size: 20px;
          font-weight: 600;
          color: #334155;
          margin-top: 24px;
          margin-bottom: 12px;
        }
        .content-section p {
          margin-bottom: 16px;
        }
        .content-section ul,
        .content-section ol {
          margin-bottom: 16px;
          padding-left: 24px;
        }
        .content-section li {
          margin-bottom: 8px;
        }
        .content-section strong {
          color: #1e293b;
          font-weight: 600;
        }

        /* Tip Box */
        .tip-box {
          display: flex;
          gap: 12px;
          background: #eff6ff;
          border-left: 4px solid #3b82f6;
          padding: 16px;
          border-radius: 8px;
          margin: 24px 0;
        }
        .tip-icon {
          font-size: 20px;
          flex-shrink: 0;
        }
        .tip-content {
          font-size: 15px;
          line-height: 1.6;
          color: #1e40af;
        }

        /* Tables */
        .fees-table,
        .schedule-table {
          margin: 24px 0;
          overflow-x: auto;
        }
        .fees-table table,
        .schedule-table table {
          width: 100%;
          border-collapse: collapse;
          background: white;
          border-radius: 8px;
          overflow: hidden;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
        }
        .fees-table th,
        .schedule-table th {
          background: #f8fafc;
          color: #1e293b;
          font-weight: 600;
          text-align: left;
          padding: 12px 16px;
          border-bottom: 2px solid #e2e8f0;
          font-size: 14px;
        }
        .fees-table td,
        .schedule-table td {
          padding: 12px 16px;
          border-bottom: 1px solid #f1f5f9;
          font-size: 15px;
        }
        .fees-table tbody tr:last-child td,
        .schedule-table tbody tr:last-child td {
          border-bottom: none;
        }
        .fees-table tbody tr:hover,
        .schedule-table tbody tr:hover {
          background: #f8fafc;
        }

        /* What's Next Section */
        .whats-next-section {
          margin-top: 60px;
          padding-top: 40px;
          border-top: 2px solid #e2e8f0;
        }
        .whats-next-section h2 {
          font-size: 28px;
          font-weight: 700;
          color: #1e293b;
          margin-bottom: 16px;
        }
        .next-steps {
          display: flex;
          flex-direction: column;
          gap: 16px;
          margin-top: 24px;
        }
        .next-step-card {
          display: flex;
          gap: 16px;
          padding: 20px;
          background: white;
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          text-decoration: none;
          transition: all 0.3s;
        }
        .next-step-card:hover {
          border-color: #6366f1;
          box-shadow: 0 4px 12px rgba(99, 102, 241, 0.15);
          transform: translateY(-2px);
        }
        .next-step-icon {
          font-size: 32px;
          flex-shrink: 0;
        }
        .next-step-content h4 {
          font-size: 18px;
          font-weight: 600;
          color: #1e293b;
          margin-bottom: 4px;
        }
        .next-step-content p {
          font-size: 14px;
          color: #64748b;
          margin: 0;
        }

        /* Was This Helpful */
        .helpful-section {
          margin-top: 60px;
          padding: 32px;
          background: #f8fafc;
          border-radius: 12px;
          text-align: center;
        }
        .helpful-section h3 {
          font-size: 20px;
          font-weight: 600;
          color: #1e293b;
          margin-bottom: 20px;
        }
        .helpful-buttons {
          display: flex;
          gap: 12px;
          justify-content: center;
          margin-bottom: 16px;
        }
        .helpful-btn {
          padding: 10px 24px;
          border: 2px solid #e2e8f0;
          background: white;
          border-radius: 8px;
          font-size: 15px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
        }
        .helpful-btn:hover {
          border-color: #6366f1;
          background: #eff6ff;
        }
        .helpful-text {
          font-size: 14px;
          color: #64748b;
          margin: 0;
        }
        .helpful-text a {
          color: #6366f1;
          text-decoration: none;
        }
        .helpful-text a:hover {
          text-decoration: underline;
        }

        /* Responsive */
        @media (max-width: 768px) {
          .help-article-page {
            padding: 100px 0 60px;
          }
          .article-header h1 {
            font-size: 32px;
          }
          .article-subtitle {
            font-size: 16px;
          }
          .content-section h2 {
            font-size: 24px;
          }
          .fees-table,
          .schedule-table {
            font-size: 14px;
          }
          .next-step-card {
            flex-direction: column;
            text-align: center;
          }
        }
      `}</style>

      <Footer />
      <GuestChatWidget />
    </div>
  );
};

export default HelpArticlePricing;
